import { request } from './client';
import type { CounterPayment, CounterPaymentIntent, OfflinePass,
  ActivityEntry, AuthTokens, Deal, Favorite, MemberCard, MyOrder, MyVoucher, NotificationPrefs,
  OrderCreated, OrderStatus, OtpChallenge, Receipt, SupportTicket, SupportTicketSummary,
  VoucherPin, WalletSummary,
  NotificationSettings, NotificationSettingsUpdate,
  VoucherShare, VoucherPass,
  PlaceTarget, PlaceWallet,
  MarketplaceCategory,
 LiveAd,
} from './types';

/**
 * Every call the shop makes, in one place.
 *
 * Components do not call `request` directly: a typo in a path here is one bug, and
 * the same typo spread across six components is six.
 */

export const api = {
  deals: (params: { category?: string; search?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.search) query.set('search', params.search);

    const suffix = query.toString();
    return request<Deal[]>(`/api/consumer/deals${suffix ? `?${suffix}` : ''}`);
  },

  deal: (id: string) => request<Deal>(`/api/consumer/deals/${id}`),

  /** The live welcome ad, or nothing (204) when none is running. Anonymous. */
  appOpenAd: () => request<LiveAd | undefined>('/api/consumer/app-open-ad'),

  /* ------------------------------------------------------------------ auth --- */

  startSignIn: (contact: { email?: string; phone?: string }) =>
    request<OtpChallenge>('/api/v1/auth/register', { method: 'POST', body: contact }),

  verifySignIn: (challengeId: string, code: string) =>
    request<AuthTokens>('/api/v1/auth/verify', {
      method: 'POST',
      body: { challengeId, code },
    }),

  /**
   * Creates nothing yet. The server holds these details against the emailed code and
   * creates the account when `verifySignIn` returns — which is why this resolves to a
   * challenge rather than to tokens.
   *
   * `dateOfBirth` is a plain YYYY-MM-DD string, never a Date. Serialising a Date
   * gives an instant in UTC, and a birthday of 1990-03-01 typed in Auckland arrives
   * as the last day of February.
   */
  signUp: (details: {
    email: string;
    fullName: string;
    dateOfBirth: string;
    password: string;
  }) => request<OtpChallenge>('/api/v1/auth/signup', { method: 'POST', body: details }),

  signInWithPassword: (email: string, password: string) =>
    request<AuthTokens>('/api/v1/auth/login', { method: 'POST', body: { email, password } }),

  /**
   * Returns a NEW token pair, because setting a password ends every other session —
   * including the one that asked. Feed the result straight back into signIn().
   *
   * `currentPassword` is omitted only by an account that does not have one yet.
   */
  setPassword: (newPassword: string, currentPassword?: string) =>
    request<AuthTokens>('/api/v1/auth/password', {
      method: 'POST',
      body: currentPassword === undefined ? { newPassword } : { currentPassword, newPassword },
    }),

  logout: (refreshToken: string) =>
    request<void>('/api/v1/auth/logout', { method: 'POST', body: { refreshToken } }),

  /* --------------------------------------------------------------- buying --- */

  /**
   * Note there is no price in the body — the server prices the order from the deal.
   * A client-supplied price is a buy-anything-for-one-cent bug.
   *
   * The idempotency key makes a double-submitted checkout one order, not two.
   */
  createOrder: (dealId: string, quantity: number, idempotencyKey: string) =>
    request<OrderCreated>('/api/consumer/orders', {
      method: 'POST',
      body: { dealId, quantity },
      idempotencyKey,
    }),

  /**
   * Also the recovery path: it asks the payment provider directly and settles the
   * order, so a webhook that never arrives does not leave the buyer stranded.
   */
  order: (orderId: string) => request<OrderStatus>(`/api/consumer/orders/${orderId}`),

  /**
   * Purchase history. No userId in the path — the server takes the buyer from the
   * token, so there is no version of this call that reads someone else's orders.
   */
  orders: () => request<MyOrder[]>('/api/consumer/orders'),

  /* ------------------------------------------------------------ membership --- */

  /** Issues a card on first call, so this works on a brand-new account. */
  membership: () => request<MemberCard>('/api/consumer/membership'),

  /**
   * Cancels the current card and issues a new one. The recovery path for a
   * photographed lock screen — the old barcode stops working immediately.
   */
  resetMembership: (reason: string) =>
    request<MemberCard>('/api/consumer/membership/reset', { method: 'POST', body: { reason } }),

  /**
   * One-minute links for the Wallet buttons. The page NAVIGATES to them, so the
   * browser receives the pass itself - see MemberCardPanel.
   */
  walletPassLinks: () =>
    request<{ appleUrl: string | null; googleUrl: string | null; expiresInSeconds: number }>(
      '/api/consumer/membership/pass-links',
      { method: 'POST' },
    ),

  /* ------------------------------------------------------------ unsubscribe --- */

  /**
   * Switches promotional email off for whoever the token in the email names. No
   * sign-in: the token is the credential, and it can do this one thing only.
   */
  unsubscribe: (token: string) =>
    request<void>(`/api/consumer/unsubscribe/${encodeURIComponent(token)}`, { method: 'POST' }),

  /* -------------------------------------------------------------- vouchers --- */

  vouchers: () => request<MyVoucher[]>('/api/consumer/vouchers'),
  voucher: (id: string) => request<MyVoucher>(`/api/consumer/vouchers/${id}`),

  /**
   * A short-lived signed pass for one of the caller's own vouchers (TC-04). 404
   * when the deployment has no signing key, or the voucher is not the caller's -
   * both mean "carry on without one".
   */
  offlinePass: (id: string) => request<OfflinePass>(`/api/consumer/vouchers/${id}/offline-pass`),

  /** TC-06: the rest of a bill the till sent to this customer's phone. */
  counterPayment: (id: string) => request<CounterPayment>(`/api/consumer/counter-payments/${id}`),

  /** Open requests for the signed-in customer - shown as a banner on their vouchers. */
  openCounterPayments: () => request<CounterPayment[]>('/api/consumer/counter-payments'),

  /** The Stripe intent for the remainder plus the chosen tip. Call again to change the tip. */
  startCounterPayment: (id: string, tipCents: number) =>
    request<CounterPaymentIntent>(`/api/consumer/counter-payments/${id}/intent`, {
      method: 'POST',
      body: { tipCents },
    }),

  /**
   * Issues a NEW PIN and returns it once. There is no call that reads the current
   * one — only a hash is stored — so this is the whole of PIN recovery.
   *
   * POST because it CHANGES the PIN. A GET would be prefetched by the browser and
   * would silently invalidate the PIN the customer is holding at a counter.
   */
  resetVoucherPin: (id: string) =>
    request<VoucherPin>(`/api/consumer/vouchers/${id}/pin`, { method: 'POST' }),

  /* ---------------------------------------------------------------- wallet --- */

  /** Balances grouped by partner, total credit, and lifetime savings. */
  walletSummary: () => request<WalletSummary>('/api/consumer/wallet/summary'),

  /**
   * The customer's own history. `businessId` and `voucherId` narrow rows already
   * restricted to them by the server — filters, not scopes.
   */
  activity: (params: { businessId?: string; voucherId?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.businessId) query.set('businessId', params.businessId);
    if (params.voucherId) query.set('voucherId', params.voucherId);
    if (params.limit) query.set('limit', String(params.limit));

    const suffix = query.toString();
    return request<ActivityEntry[]>(`/api/consumer/wallet/activity${suffix ? `?${suffix}` : ''}`);
  },

  receipt: (redemptionId: string) =>
    request<Receipt>(`/api/consumer/wallet/receipts/${redemptionId}`),

  /** "Deals by text": record consent to marketing SMS. Anonymous; the server normalises the number. */
  smsOptIn: (body: { phone: string; consent: boolean; consentText: string; pageUrl: string }) =>
    request<void>('/api/consumer/sms-optin', { method: 'POST', body }),

  /* ------------------------------------------------------------- favourites --- */

  favorites: () => request<Favorite[]>('/api/consumer/favorites'),

  /**
   * PUT, because favouriting is idempotent — the same call twice leaves one row
   * and two successes. That is what makes the optimistic heart safe: the UI can
   * fill in immediately and a double tap cannot produce an error to undo.
   */
  addFavorite: (businessId: string) =>
    request<void>(`/api/consumer/favorites/${businessId}`, { method: 'PUT' }),

  removeFavorite: (businessId: string) =>
    request<void>(`/api/consumer/favorites/${businessId}`, { method: 'DELETE' }),

  /* ------------------------------------------------------------ preferences --- */

  notificationPrefs: () =>
    request<NotificationPrefs>('/api/consumer/preferences/notifications'),

  /** The complete set, not a patch — see the controller for why. */
  updateNotificationPrefs: (prefs: Omit<NotificationPrefs, 'pushAvailable'>) =>
    request<NotificationPrefs>('/api/consumer/preferences/notifications', {
      method: 'PUT',
      body: prefs,
    }),

  /* ---------------------------------------------------------------- support --- */

  tickets: () => request<SupportTicketSummary[]>('/api/consumer/support/tickets'),

  ticket: (id: string) => request<SupportTicket>(`/api/consumer/support/tickets/${id}`),

  createTicket: (body: {
    subject: string;
    body: string;
    category: string;
    voucherId?: string;
    orderId?: string;
  }) => request<SupportTicket>('/api/consumer/support/tickets', { method: 'POST', body }),

  replyToTicket: (id: string, body: string) =>
    request<SupportTicket>(`/api/consumer/support/tickets/${id}/replies`, {
      method: 'POST',
      body: { body },
    }),

  /* ------------------------------------------------------- notifications --- */

  /**
   * The catalogue-driven settings screen.
   *
   * REPLACES the four-switch `preferences/notifications` pair above for display
   * purposes, but does NOT replace their storage: this endpoint reads those rows as
   * its fallback, so somebody who turned promotions off years ago stays off. The
   * older calls are kept because they are still the API a mobile build on an
   * earlier release is talking to.
   */
  notificationSettings: () =>
    request<NotificationSettings>('/api/consumer/notifications/settings'),

  /** Sparse — only the switches that moved. */
  updateNotificationSettings: (body: NotificationSettingsUpdate) =>
    request<NotificationSettings>('/api/consumer/notifications/settings', {
      method: 'PUT',
      body,
    }),

  /* --------------------------------------------------------- voucher share --- */

  /** What is live for this voucher, if anything. Never returns a URL. */
  voucherShare: (voucherId: string) =>
    // 204 (no live share) comes back from request() as undefined, not null. Every
    // caller tests `!== null`, so an unmapped undefined read `.expiresAt` off
    // nothing and blanked the whole voucher page for any voucher never shared.
    request<VoucherShare | null>(`/api/consumer/vouchers/${voucherId}/share`).then((s) => s ?? null),

  /**
   * Make a link. THE URL COMES BACK ONCE — the token is stored only as a hash, so
   * a caller that discards this response cannot ask for it again.
   */
  createVoucherShare: (voucherId: string) =>
    request<VoucherShare>(`/api/consumer/vouchers/${voucherId}/share`, { method: 'POST' }),

  /** Stop every live link working. The lost-phone remedy. */
  revokeVoucherShare: (voucherId: string) =>
    request<void>(`/api/consumer/vouchers/${voucherId}/share`, { method: 'DELETE' }),

  /** ANONYMOUS. 404 for unknown, expired and revoked alike. */
  voucherPass: (token: string) =>
    request<VoucherPass>(`/api/v1/public/pass/${encodeURIComponent(token)}`),

  /* ------------------------------------------------------------- place --- */

  /** Anonymous: which business and branch a printed sticker belongs to. */
  place: (token: string) =>
    request<PlaceTarget>(`/api/v1/public/at/${encodeURIComponent(token)}`),

  /** Signed in: the same place, plus what you hold there. */
  placeWallet: (token: string) =>
    request<PlaceWallet>(`/api/consumer/at/${encodeURIComponent(token)}`),

  /** Categories that actually have live deals, most populous first. */
  dealCategories: () =>
    request<MarketplaceCategory[]>('/api/consumer/deals/categories'),
};
